import { createSelector } from '@reduxjs/toolkit';
import {
  TypedUseQueryHookResult,
  skipToken,
} from '@reduxjs/toolkit/dist/query/react';

import { parseApiResponse } from '../materials.utils';

import { AxiosBaseQueryFunction } from '~/api/axiosBaseQuery';
import {
  MaterialsQueryParams,
  useFetchMaterialsQuery,
} from '~/api/easy-estimator-api/materials-api/materialsService';
import { useSelectedStore } from '~/api/easy-estimator-api/stores-api/useSelectedStore';
import { shouldFetchFn } from '~/api/utils';
import { MaterialModel, MaterialsAPIItem } from '~/core/models';
import { useAppSelector } from '~/redux/hooks';
import { selectZipcode } from '~/redux/slices/zipcode';

const emptyArray = [] satisfies MaterialModel[];

type useMaterialsReturn = Omit<
  TypedUseQueryHookResult<
    MaterialModel[],
    MaterialsQueryParams,
    AxiosBaseQueryFunction
  >,
  'data' | 'isFetching' | 'isError'
> & {
  materials: MaterialModel[];
  isFetchingMaterials: boolean;
  isErrorMaterials: boolean;
};

export const parseMaterials = createSelector(
  (data?: MaterialsAPIItem[]) => data,
  (data) => (!!data && data.length > 0 ? parseApiResponse(data) : emptyArray)
);

export const useMaterials = (): useMaterialsReturn => {
  const selectedStore = useSelectedStore();
  const zipCode = useAppSelector(selectZipcode);

  const shouldFetch = shouldFetchFn({ args: [zipCode, selectedStore] });

  const {
    materials,
    isFetching: isFetchingMaterials,
    isError: isErrorMaterials,
    ...rest
  } = useFetchMaterialsQuery(
    shouldFetch
      ? { params: { zipCode, storeId: selectedStore?.companyId } }
      : skipToken,
    {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        materials: parseMaterials(data),
      }),
    }
  );

  return {
    materials,
    isFetchingMaterials,
    isErrorMaterials,
    ...rest,
  };
};
